import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { MOCK_DESIGNS } from '../../lib/mockData';
import SafeIcon from '../../common/SafeIcon';
import NavButtons from './NavButtons';

const PAGE_SIZE = 10;

function ZoomModal({ design, onClose, onSelect }) {
  const [index, setIndex] = useState(0);
  if (!design) return null;
  // Index 0: grid image (tight crop, shown with dimensions overlay)
  // Index 1: detail/secondary image (image_url_2)
  // image_url (mockup with padding) is only used for the flower overlay — not shown here
  const gridImage = design.image_url_grid || design.image_url;
  const images = [gridImage, design.image_url_2].filter(Boolean);

  const handleSelect = () => {
    onSelect(design);
    onClose();
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0 }} 
      animate={{ opacity: 1 }} 
      exit={{ opacity: 0 }} 
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-charcoal/90 p-4 backdrop-blur-sm"
    >
      <motion.div 
        initial={{ scale: 0.9, opacity: 0 }} 
        animate={{ scale: 1, opacity: 1 }} 
        exit={{ scale: 0.9, opacity: 0 }} 
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-4xl w-full bg-white rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row h-full max-h-[85vh]"
      >
        {/* Precise Close Button - Large Hit Area */}
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 z-50 bg-white/90 backdrop-blur-md p-3 rounded-full text-charcoal shadow-md border border-sage-100 hover:bg-white hover:text-blush-500 transition-all active:scale-90"
          aria-label="Close Zoom View"
        >
          <SafeIcon name="X" className="text-xl block" />
        </button>

        {/* Image area */}
        <div className="flex-1 bg-sage-50 overflow-hidden relative flex items-center justify-center">

          {index === 0 ? (
            /* Measurement layout — measurements positioned just outside the image */
            <div className="relative w-full h-full flex items-center justify-center p-12">
              <div className="relative inline-flex items-center justify-center w-full h-full">
                {/* Main image - fills available space */}
                <img 
                  src={images[index]} 
                  alt={design.name} 
                  className="w-full h-full object-contain" 
                />
                
                {/* Height measurement - positioned just inside the left edge, clear of the arrow */}
                <div className="absolute left-8 top-0 bottom-0 flex items-center">
                  <div className="relative flex flex-col items-center h-[80%]">
                    {/* Top tick */}
                    <div className="w-2 h-px bg-sage-500"></div>
                    {/* Vertical line */}
                    <div className="flex-1 w-px bg-sage-500 relative flex items-center justify-center">
                      {/* Label - rotated */}
                      <span className="absolute bg-charcoal text-white text-sm font-bold uppercase tracking-wider whitespace-nowrap -rotate-90 px-2 py-1 rounded shadow-sm">
                        10" HEIGHT
                      </span>
                    </div>
                    {/* Bottom tick */}
                    <div className="w-2 h-px bg-sage-500"></div>
                  </div>
                </div>

                {/* Width measurement - positioned below the image with more margin from bottom edge */}
                <div className="absolute left-0 right-0 bottom-0 translate-y-5 flex justify-center">
                  <div className="relative flex flex-row items-center w-[60%]">
                    {/* Left tick */}
                    <div className="h-2 w-px bg-sage-500"></div>
                    {/* Horizontal line */}
                    <div className="flex-1 h-px bg-sage-500 relative flex items-center justify-center">
                      {/* Label */}
                      <span className="absolute bg-charcoal text-white text-sm font-bold uppercase tracking-wider whitespace-nowrap px-2 py-1 rounded shadow-sm">
                        3.5" WIDE
                      </span>
                    </div>
                    {/* Right tick */}
                    <div className="h-2 w-px bg-sage-500"></div>
                  </div>
                </div>
              </div>
            </div>

          ) : (
            /* No measurements on secondary images — simple centered image with arrows */
            <div className="w-full h-full flex items-center justify-center relative">
              <img src={images[index]} alt={design.name} className="w-full h-full object-contain p-8" />
            </div>
          )}
        </div>

        {/* Arrows constrained to the image panel only — not the full modal width */}
        {images.length > 1 && (
          <div className="absolute inset-y-0 left-0 md:right-80 right-0 flex items-center justify-between pointer-events-none z-20 px-2">
            <button 
              onClick={() => setIndex(prev => (prev === 0 ? images.length - 1 : prev - 1))} 
              className="p-3 rounded-full bg-black/80 backdrop-blur-sm shadow-lg pointer-events-auto hover:bg-black transition-all active:scale-90"
            >
              <SafeIcon name="ChevronLeft" className="text-xl text-white" />
            </button>
            <button 
              onClick={() => setIndex(prev => (prev === images.length - 1 ? 0 : prev + 1))} 
              className="p-3 rounded-full bg-black/80 backdrop-blur-sm shadow-lg pointer-events-auto hover:bg-black transition-all active:scale-90"
            >
              <SafeIcon name="ChevronRight" className="text-xl text-white" />
            </button>
          </div>
        )}

        <div className="p-8 md:w-80 bg-white border-l border-sage-100 flex flex-col justify-center">
          <p className="text-[10px] text-blush-400 uppercase tracking-widest font-bold mb-2">Vase Design</p>
          <h3 className="font-serif text-3xl text-charcoal mb-4">{design.name}</h3>
          {design.description && (
            <div className="mb-8">
              <p className="text-sage-500 text-sm leading-relaxed italic">
                {design.description}
              </p>
            </div>
          )}
          <button 
            onClick={handleSelect} 
            className="btn-primary w-full !min-w-0 !py-4"
          >
            Select This Design
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

function DesignTile({ design, isSelected, onSelect, onZoom }) {
  const images = [design.image_url, design.image_url_2].filter(Boolean);
  // Use grid thumbnail if available, otherwise fall back to main image
  const displayImage = design.image_url_grid || images[design.main_image_index || 0] || images[0];

  return (
    <div className="flex flex-col gap-3 group">
      <div 
        onClick={() => onZoom(design)}
        className={`relative w-full rounded-3xl overflow-hidden aspect-[3/4] transition-all duration-300 bg-sage-50/50 cursor-pointer ${
          isSelected ? 'ring-4 ring-blush-300 shadow-glow' : 'ring-1 ring-sage-100 hover:ring-blush-200 hover:shadow-soft'
        }`}
      >
        <img 
          src={displayImage} 
          alt={design.name} 
          className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" 
        />
        {isSelected && (
          <div className="absolute inset-0 bg-blush-300/5 flex items-center justify-center pointer-events-none">
            <div className="bg-white rounded-full p-2 shadow-md">
              <SafeIcon name="Check" className="w-5 h-5 text-blush-400" />
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col items-center gap-3">
        <div className="flex items-center w-full px-1">
          <button 
            onClick={() => onSelect(design)}
            className={`w-full flex items-center justify-center gap-1.5 py-2 rounded-full transition-all active:scale-95 text-[10px] font-bold uppercase tracking-widest shadow-sm ${
              isSelected 
                ? 'bg-blush-400 text-white' 
                : 'bg-blush-300 text-white hover:bg-blush-400'
            }`}
          >
            {isSelected ? (
              <>
                <SafeIcon name="Check" className="text-xs" />
                Selected
              </>
            ) : (
              'Select'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Step1DesignPicker({ formData, updateFormData, onNext, onBack }) {
  const isNextDay = formData.delivery_type === 'next_day';
  const [categories, setCategories] = useState([]);
  const [designs, setDesigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [expandedParent, setExpandedParent] = useState(null);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [zoomedDesign, setZoomedDesign] = useState(null);
  const [canScrollLeft, setCanScrollLeft]   = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const pillsRowRef = useRef(null);
  const categoryBarRef = useRef(null);

  // Update arrow visibility based on scroll position
  function updateScrollState() {
    const el = pillsRowRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }

  // Scroll hint on mount — nudges right then snaps back so user sees it's scrollable
  useEffect(() => {
    const el = pillsRowRef.current;
    if (!el) return;
    updateScrollState();
    // Only hint if there's actually overflow
    const timer = setTimeout(() => {
      if (el.scrollWidth > el.clientWidth + 8) {
        el.scrollTo({ left: 80, behavior: 'smooth' });
        setTimeout(() => el.scrollTo({ left: 0, behavior: 'smooth' }), 600);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [categories]); // runs once categories have loaded

  useEffect(() => {
    async function fetchCategories() {
      if (isSupabaseConfigured()) {
        let catQuery = supabase
          .from('design_categories_1739812345679')
          .select('*')
          .eq('active', true)
          .order('sort_order');

        // next_day: only show next_day_only categories
        // scheduled: show all categories (no filter)
        if (isNextDay) {
          catQuery = catQuery.eq('next_day_only', true);
        }

        const { data: allCats } = await catQuery;

        if (!allCats?.length) {
          setCategories([]);
          setActiveCategory(null);
          return;
        }

        // Fetch all active design category arrays in one query
        const { data: activeDesigns } = await supabase
          .from('designs')
          .select('category')
          .eq('active', true);

        const usedCategoryIds = new Set(
          (activeDesigns || []).flatMap(d => d.category || [])
        );

        const parents  = allCats.filter(c => !c.parent_id);
        const children = allCats.filter(c =>  c.parent_id);

        // Keep a category if:
        //   - it's a child with designs, OR
        //   - it's a parent with at least one child that has designs, OR
        //   - it's a leaf parent (no children) with designs directly
        const keptIds = new Set();
        children.forEach(ch => {
          if (usedCategoryIds.has(ch.id)) {
            keptIds.add(ch.id);
            keptIds.add(ch.parent_id); // keep the parent too
          }
        });
        parents.forEach(p => {
          const hasKids = children.some(ch => ch.parent_id === p.id);
          if (!hasKids && usedCategoryIds.has(p.id)) keptIds.add(p.id); // leaf parent
        });

        const nonEmpty = allCats.filter(c => keptIds.has(c.id));

        if (nonEmpty.length > 0) {
          setCategories(nonEmpty);
          // Auto-select: pick first child of first parent, or first leaf if no children
          const firstParent = nonEmpty.find(c => !c.parent_id);
          const firstChild  = firstParent
            ? nonEmpty.find(c => c.parent_id === firstParent.id)
            : null;
          const firstActive = firstChild || firstParent;
          setActiveCategory(firstActive?.id || null);
          // If the first category has children, expand it
          if (firstChild) setExpandedParent(firstParent.id);
        } else {
          setCategories([]);
          setActiveCategory(null);
        }
      }
    }
    fetchCategories();
  }, [isNextDay]);

  const fetchDesigns = useCallback(async () => {
    if (!activeCategory) return;

    // If the active category is a parent (has children), don't load —
    // designs only live in subcategories. The drawer auto-selects a child.
    const hasChildren = categories.some(c => c.parent_id === activeCategory);
    if (hasChildren) return;

    setLoading(true);
    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    if (isSupabaseConfigured()) {
      let query = supabase
        .from('designs')
        .select('*', { count: 'exact' })
        .eq('active', true)
        .contains('category', [activeCategory])
        .order('sort_order')
        .range(from, to);

      // If next-day order, only show next-day eligible designs
      if (isNextDay) {
        query = query.eq('next_day_available', true);
      }

      const { data, count } = await query;

      setDesigns(data || []);
      setTotalCount(count || 0);
    } else {
      let filtered = MOCK_DESIGNS.filter(d => d.category.includes(activeCategory));
      // In mock mode, treat all designs as next_day_available for demo
      setDesigns(filtered.slice(from, to + 1));
      setTotalCount(filtered.length);
    }
    setLoading(false);
  }, [activeCategory, page, categories]);

  useEffect(() => {
    fetchDesigns();
  }, [fetchDesigns]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }} 
      animate={{ opacity: 1, x: 0 }} 
      exit={{ opacity: 0, x: -20 }} 
      className="space-y-10"
    >
      <div className="text-center space-y-3">
        <h2 className="text-4xl font-serif text-charcoal">Choose a Vase Design</h2>
        <p className="text-sage-500 text-lg">Pick a theme and browse our available wraps.</p>
        {isNextDay && (
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-sm font-bold px-5 py-2.5 rounded-full">
            <span>⚡</span> Showing next-day eligible designs only
          </div>
        )}

      </div>

      {/* ── Category picker — grouped parents + expandable subcategories ── */}
      <div ref={categoryBarRef} className="space-y-3">

        {/* Parent row with scroll affordance */}
        <div className="relative">
          {canScrollLeft && (
            <>
              <div className="absolute left-0 top-0 bottom-4 w-16 bg-gradient-to-r from-warmwhite to-transparent z-10 pointer-events-none" />
              <button
                onClick={() => pillsRowRef.current?.scrollBy({ left: -200, behavior: 'smooth' })}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-20 mb-4 w-8 h-8 flex items-center justify-center rounded-full bg-white border border-sage-200 shadow-sm text-sage-500 hover:text-charcoal transition-all"
                aria-label="Scroll left"
              >
                <SafeIcon name="ChevronLeft" className="text-sm" />
              </button>
            </>
          )}
          {canScrollRight && (
            <>
              <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-warmwhite to-transparent z-10 pointer-events-none" />
              <button
                onClick={() => pillsRowRef.current?.scrollBy({ left: 200, behavior: 'smooth' })}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-20 mb-4 w-8 h-8 flex items-center justify-center rounded-full bg-white border border-sage-200 shadow-sm text-sage-500 hover:text-charcoal transition-all"
                aria-label="Scroll right"
              >
                <SafeIcon name="ChevronRight" className="text-sm" />
              </button>
            </>
          )}

          <div
            ref={pillsRowRef}
            onScroll={updateScrollState}
            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3 pb-2 px-2"
          >
            {(() => {
              // Separate top-level parents from children
              const parents  = categories.filter(c => !c.parent_id);
              const children = categories.filter(c =>  c.parent_id);
              // If no parents exist (all flat / legacy), show everything as a flat row
              const topLevel = parents.length > 0 ? parents : categories;

              return topLevel.map((cat) => {
                const kids = children.filter(ch => ch.parent_id === cat.id);
                const hasKids = kids.length > 0;
                const isParentActive = activeCategory === cat.id || kids.some(k => k.id === activeCategory);
                const isExpanded = expandedParent === cat.id;

                return (
                  <button
                    key={cat.id}
                    onClick={() => {
                      if (hasKids) {
                        const opening = expandedParent !== cat.id;
                        setExpandedParent(opening ? cat.id : null);
                        if (opening) {
                          // Auto-select first non-empty child when drawer opens
                          const firstChild = kids[0];
                          if (firstChild) {
                            setActiveCategory(firstChild.id);
                            setPage(0);
                          }
                        } else {
                          // Collapsing — clear active so no stale grid shows
                          setActiveCategory(null);
                          setDesigns([]);
                        }
                      } else {
                        // Leaf category — select directly
                        setExpandedParent(null);
                        setActiveCategory(cat.id);
                        setPage(0);
                      }
                    }}
                    className={`snap-start shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 flex items-center gap-1.5 ${
                      isParentActive
                        ? 'bg-charcoal text-white shadow-md'
                        : 'bg-white border border-sage-200 text-sage-500 hover:border-sage-300'
                    }`}
                  >
                    {cat.label}
                    {hasKids && (
                      <SafeIcon
                        name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                        className="text-xs opacity-70"
                      />
                    )}
                  </button>
                );
              });
            })()}
          </div>
        </div>

        {/* Subcategory drawer — slides in below parent row when a parent is expanded */}
        <AnimatePresence>
          {expandedParent && (() => {
            const kids = categories.filter(c => c.parent_id === expandedParent);
            if (!kids.length) return null;
            return (
              <motion.div
                key={expandedParent}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{   opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 px-2 pt-1 pb-2 bg-sage-50/60 rounded-2xl border border-sage-100">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-sage-400 self-center pr-1">
                    {categories.find(c => c.id === expandedParent)?.label}:
                  </span>
                  {kids.map(child => (
                    <button
                      key={child.id}
                      onClick={() => {
                        setActiveCategory(child.id);
                        setPage(0);
                      }}
                      className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 ${
                        activeCategory === child.id
                          ? 'bg-blush-300 text-white shadow-sm'
                          : 'bg-white border border-sage-200 text-sage-500 hover:border-blush-200 hover:text-blush-400'
                      }`}
                    >
                      {child.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-10 gap-x-6">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-3 animate-pulse">
              <div className="w-full aspect-[3/4] rounded-3xl bg-sage-100" />
              <div className="h-3 bg-sage-100 rounded-full mx-4" />
              <div className="h-8 bg-sage-100 rounded-full mx-1" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-10 gap-x-6">
          {designs.map(design => (
            <DesignTile 
              key={design.id} 
              design={design} 
              isSelected={formData.design?.id === design.id}
              onSelect={d => updateFormData({ design: d, selected_category: activeCategory })}
              onZoom={setZoomedDesign}
            />
          ))}
        </div>
      )}

      {/* Design count + pagination */}
      <div className="flex flex-col items-center gap-4 pt-2">
        <p className="text-xs text-sage-400 font-medium">
          Showing {Math.min((page + 1) * PAGE_SIZE, totalCount)} of{' '}
          <span className="font-bold text-charcoal">{totalCount} designs</span>
          {' '}in this category
        </p>

        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-6">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary !py-2.5 !px-6"
            >
              ← Prev
            </button>
            <span className="text-sm font-medium text-sage-400">Page {page + 1} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="btn-secondary !py-2.5 !px-6"
            >
              Next →
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {formData.design && (
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-[2.5rem] border-2 border-blush-100 p-6 flex items-center gap-6 shadow-glow"
          >
            <div className="w-16 aspect-[3/4] rounded-2xl overflow-hidden bg-sage-50 border border-sage-100 shrink-0">
              <img 
                src={formData.design.main_image_index === 1 ? formData.design.image_url_2 : formData.design.image_url} 
                className="w-full h-full object-contain" 
                alt="Selected Design" 
              />
            </div>
            <div className="flex-1">
              <p className="text-[10px] text-blush-400 uppercase tracking-widest font-bold">Selected Design</p>
              <p className="font-serif text-2xl text-charcoal">{formData.design.name}</p>
            </div>
            <div className="bg-blush-300 text-white rounded-full p-2 shadow-md">
              <SafeIcon name="Check" className="w-5 h-5" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <NavButtons 
        onNext={onNext} 
        onBack={onBack}
        disabled={!formData.design} 
        showBack={true} 
        nextLabel="Customize Arrangement" 
      />

      <AnimatePresence>
        {zoomedDesign && (
          <ZoomModal 
            design={zoomedDesign} 
            onClose={() => setZoomedDesign(null)} 
            onSelect={(d) => updateFormData({ design: d, selected_category: activeCategory })}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
